import React, { Component } from 'react';
import PropTypes from 'prop-types';

// Redux
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { setAlert } from '../../../redux/actions/alertActions';
import { loadFleet, dismissAttention, selectAttention } from '../../../redux/actions/fleetActions';

// Server
import { listRooms, listEvents } from '../../../api';
import { ageOf } from '../../../lib/attention';

// Components
import PageHeader from '../../../components/PageHeader';
import StatCard from '../../../components/StatCard';
import StatusChip from '../../../components/StatusChip';
import ActivityFeed from '../../../components/ActivityFeed';
import DeviceHealthBar from '../../../components/DeviceHealthBar';

// Constants
import { AlertTypes, DEFAULT, lowBatteryPercent } from '../../../constants';
import { T, FONT_MONO, SET_ORDER, INDICATOR_TEXT } from '../../../constants/tokens';

/**
 * Campus overview: readiness at a glance, the spread of room statuses, and
 * what needs attention.
 *
 * The numbers are derived from the rooms, hubs and staff; "Alert updates" is
 * the event log, what happened, while "Needs attention" is what is wrong now:
 * the same list as the header bell. Hubs and staff come from the store, which
 * the app keeps current while signed in. The design's district rollup needs multi-campus, so it is not
 * here yet.
 */
class Overview extends Component {
  constructor(props) {
    super(props);

    this._ismounted = false;
    this.state = { rooms: null, events: null };
  }

  componentDidMount() {
    this._ismounted = true;
    this.load();
    this.loadEvents();
    // The feed is the part of this screen that changes minute to minute.
    this.eventTimer = setInterval(this.loadEvents, 15000);
  }

  componentWillUnmount() {
    this._ismounted = false;
    clearInterval(this.eventTimer);
  }

  loadEvents = () => {
    listEvents({ limit: 8 })
      .then((events) => {
        if (this._ismounted) this.setState({ events });
      })
      .catch(() => {
        // The rest of the screen stands without the feed; it retries on the next tick.
        if (this._ismounted && this.state.events == null) this.setState({ events: [] });
      });
  }

  load = () => {
    const set = (key) => (value) => {
      if (this._ismounted) this.setState({ [key]: value });
    };
    const fail = (message) => (error) => this.props.setError(error ? error : message);

    listRooms().then(set('rooms')).catch(fail('Could not retrieve rooms.'));
    // Fresh on arrival rather than waiting for the next background poll.
    this.props.loadFleet().catch(fail('Could not retrieve hubs and staff.'));
  }

  render() {
    const rooms = this.state.rooms ?? [];
    const hubs = this.props.hubs ?? [];
    const staff = this.props.staff ?? [];
    const loading = this.state.rooms == null || this.props.hubs == null || this.props.staff == null;

    const hubsConnected = hubs.filter((hub) => hub.getIsConnected()).length;
    const hubsOffline = hubs.length - hubsConnected;
    const schoolStatus = this.props.schoolStatus ?? AlertTypes.UNKNOWN;
    const badges = staff.map((s) => s.getBadge()).filter(Boolean);
    const badgesConnected = badges.filter((b) => b.getIsConnected()).length;
    const lowBadges = badges.filter((b) => b.getBatteryLevel() != null && b.getBatteryLevel() < lowBatteryPercent()).length;
    const withoutBadge = staff.filter((s) => s.getBadge() == null).length;

    // Health bars: working, low battery, offline; and a chip per device that
    // is not working, which opens it on Devices.
    const openDevice = (tab, serial) => this.props.history.push(
      '/dashboard/devices?tab=' + encodeURIComponent(tab) + '&select=' + encodeURIComponent(serial)
    );
    const hubsOfflineList = hubs.filter((hub) => !hub.getIsConnected());
    const isLow = (b) => b.getBatteryLevel() != null && b.getBatteryLevel() < lowBatteryPercent();
    const badgesOffline = badges.filter((b) => !b.getIsConnected());
    const badgesLowOnline = badges.filter((b) => b.getIsConnected() && isLow(b));
    const hubHealth = (
      <DeviceHealthBar
        counts={{ ok: hubsConnected, bad: hubsOfflineList.length }}
        issues={hubsOfflineList.map((hub) => ({
          key: hub.getSerialNumber(),
          label: hub.getSerialNumber() + ' · offline',
          tone: 'bad',
          onOpen: () => openDevice('Hubs', hub.getSerialNumber())
        }))}
        onMore={() => this.props.history.push('/dashboard/devices?tab=Hubs')}
      />
    );
    const badgeHealth = (
      <DeviceHealthBar
        counts={{ ok: badgesConnected - badgesLowOnline.length, warn: badgesLowOnline.length, bad: badgesOffline.length }}
        issues={badgesOffline.map((b) => ({
          key: b.getSerialNumber(),
          label: b.getSerialNumber() + ' · offline',
          tone: 'bad',
          onOpen: () => openDevice('Badges', b.getSerialNumber())
        })).concat(badgesLowOnline.map((b) => ({
          key: b.getSerialNumber(),
          label: b.getSerialNumber() + ' · ' + Math.floor(b.getBatteryLevel()) + '%',
          tone: 'warn',
          onOpen: () => openDevice('Badges', b.getSerialNumber())
        })))}
        onMore={() => this.props.history.push('/dashboard/devices?tab=Badges')}
      />
    );

    // Rooms whose status is not Normal are the ones worth counting.
    const normalName = AlertTypes.NORMAL.getDashboardStatus();
    const notNormal = rooms.filter((room) => {
      const name = room.getAlertStatus()?.getDashboardStatus();
      return name != null && name !== normalName;
    }).length;

    // Status spread, ordered most severe first, zero rows dropped.
    const breakdown = SET_ORDER
      .map((key) => AlertTypes[key])
      .filter(Boolean)
      .concat(AlertTypes.UNKNOWN)
      .map((status) => ({
        status,
        count: rooms.filter((room) => {
          const name = room.getAlertStatus()?.getDashboardStatus();
          if (status === AlertTypes.UNKNOWN) return name == null;
          return name === status.getDashboardStatus();
        }).length
      }))
      .filter((row) => row.count > 0);

    const attention = this.props.attention;

    return (
      <>
        <PageHeader
          title='Overview'
          subtitle={loading
            ? 'Loading…'
            : rooms.length + ' rooms · ' + hubs.length + ' hubs · ' + staff.length + ' staff'}
        />

        {/* Readiness. The design leads each tile with a ratio and follows it
            with what that ratio means, rather than four bare counts. */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 24,
            marginBottom: 48
          }}
        >
          <StatCard
            label='School status'
            value={schoolStatus.getDashboardStatus()}
            detail='Set it from the menu or a room panel'
            accent={schoolStatus.getColor().main === DEFAULT.main ? null : schoolStatus.getColor().main}
          />
          <StatCard
            label='Hubs'
            value={hubsConnected + ' / ' + hubs.length}
            detail={hubsOffline > 0 ? 'Locating degraded where offline' : 'All hubs reporting'}
            accent={hubsOffline > 0 ? INDICATOR_TEXT.error : INDICATOR_TEXT.success}
            onClick={() => this.props.history.push('/dashboard/devices?tab=Hubs')}
            footer={hubHealth}
          />
          <StatCard
            label='Badges'
            value={badgesConnected + ' / ' + badges.length}
            detail={withoutBadge + (withoutBadge === 1 ? ' staff member without a badge' : ' staff without a badge')}
            accent={badgesOffline.length > 0 ? INDICATOR_TEXT.error : (lowBadges > 0 ? INDICATOR_TEXT.warning : INDICATOR_TEXT.success)}
            onClick={() => this.props.history.push('/dashboard/devices?tab=Badges')}
            footer={badgeHealth}
          />
          <StatCard
            label='Rooms'
            value={notNormal + ' not normal'}
            detail={'of ' + rooms.length + (rooms.length === 1 ? ' room' : ' rooms') + ' on campus'}
            accent={notNormal > 0 ? INDICATOR_TEXT.accent : null}
            onClick={() => this.props.history.push('/dashboard/map')}
          />
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: this.props.windowWidth >= 900 ? 'minmax(0, 1fr) minmax(0, 1fr)' : 'minmax(0, 1fr)',
            gap: 24,
            alignItems: 'start'
          }}
        >
          {/* Status spread */}
          <section
            style={{
              background: T.surfaceCard,
              border: '1px solid ' + T.hairline,
              borderRadius: 6,
              padding: 20
            }}
          >
            <h2 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, lineHeight: '24px', color: T.ink }}>
              Room status
            </h2>
            {breakdown.length === 0 &&
              <div style={{ fontSize: 14, color: T.muted }}>No rooms configured yet.</div>}
            {breakdown.map(({ status, count }) => {
              const share = rooms.length > 0 ? Math.round((count / rooms.length) * 100) : 0;
              return (
                <div key={status.getDashboardStatus()} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 9999, background: status.getColor().main, flex: 'none' }} />
                    <span style={{ flex: 1, minWidth: 0, fontSize: 14, color: T.ink }}>
                      {status.getDashboardStatus()}
                    </span>
                    <span style={{ fontFamily: FONT_MONO, fontSize: 12, color: T.muted }}>
                      {count}
                    </span>
                  </div>
                  <div style={{ height: 6, borderRadius: 9999, background: T.surfaceSoft, overflow: 'hidden' }}>
                    <div style={{ width: share + '%', height: '100%', background: status.getColor().main }} />
                  </div>
                </div>
              );
            })}
          </section>

          {/* Alert updates: the event log, newest first */}
          <section
            style={{
              background: T.surfaceCard,
              border: '1px solid ' + T.hairline,
              borderRadius: 6,
              padding: 20,
              minWidth: 0
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 8 }}>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, lineHeight: '24px', color: T.ink }}>
                Alert updates
              </h2>
              <div style={{ flex: 1 }} />
              <button
                type='button'
                onClick={() => this.props.history.push('/dashboard/alerts')}
                style={{ border: 0, background: 'transparent', padding: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 500, color: T.ink }}
              >
                All alerts →
              </button>
            </div>
            <ActivityFeed
              events={this.state.events}
              onOpenAlert={(id) => this.props.history.push('/dashboard/alerts/' + id)}
              emptyText='No alerts or device changes recorded yet.'
            />
          </section>

          {/* Needs attention */}
          <section
            style={{
              gridColumn: '1 / -1',
              background: T.surfaceCard,
              border: '1px solid ' + T.hairline,
              borderRadius: 6,
              padding: 20
            }}
          >
            <h2 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, lineHeight: '24px', color: T.ink }}>
              Needs attention
            </h2>
            {attention.length === 0 &&
              <div style={{ fontSize: 14, color: T.muted }}>
                {loading ? 'Loading…' : 'Nothing needs attention.'}
              </div>}
            {attention.map((item) => (
              <div
                key={item.id}
                style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 0', borderBottom: '1px solid ' + T.hairlineSoft }}
              >
                <div
                  role='button'
                  tabIndex={0}
                  onClick={() => this.props.history.push(item.link)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      this.props.history.push(item.link);
                    }
                  }}
                  style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                >
                  <StatusChip tone={item.tone} label={item.title} />
                  {item.meta &&
                    <div style={{ fontFamily: FONT_MONO, fontSize: 12, color: T.mutedSoft, marginTop: 2 }}>
                      {item.meta}
                    </div>}
                </div>
                {ageOf(item.since) &&
                  <span style={{ flex: 'none', fontFamily: FONT_MONO, fontSize: 12, lineHeight: '24px', color: T.muted }}>
                    {ageOf(item.since)}
                  </span>}
                <button
                  type='button'
                  aria-label={'Dismiss: ' + item.title}
                  onClick={() => this.props.dismissAttention(item.id)}
                  style={{ flex: 'none', width: 24, height: 24, border: 0, background: 'transparent', color: T.muted, fontSize: 16, cursor: 'pointer', padding: 0 }}
                >
                  &times;
                </button>
              </div>
            ))}
          </section>
        </div>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  windowWidth: state.ui.windowSize.width,
  schoolStatus: state.status.schoolStatus,
  hubs: state.fleet.hubs,
  staff: state.fleet.staff,
  attention: selectAttention(state)
});

const mapDispatchToProps = (dispatch) => ({
  setError: (msg) => setAlert(dispatch, msg),
  loadFleet: () => loadFleet(dispatch),
  dismissAttention: (id) => dispatch(dismissAttention(id))
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Overview));

Overview.propTypes = {
  setError: PropTypes.func.isRequired,
  loadFleet: PropTypes.func.isRequired,
  dismissAttention: PropTypes.func.isRequired,
  hubs: PropTypes.array,
  staff: PropTypes.array,
  attention: PropTypes.array.isRequired,
  windowWidth: PropTypes.number,
  schoolStatus: PropTypes.object,
  history: PropTypes.object
};
